<template>
  <dl class="line-item-search-result m-0">
    <div v-if="renderedHandling" class="handling">
      <dt class="visually-hidden">{{ t('lineItems.handling') }}</dt>
      <dd class="m-0">{{ renderedHandling }}</dd>
    </div>

    <div v-if="renderedWeight" class="weight">
      <dt class="visually-hidden">{{ t('lineItems.weight') }}</dt>
      <dd class="m-0">{{ renderedWeight }}</dd>
    </div>

    <div v-if="renderedLinearFeet" class="text-gray-darker">
      <dt class="visually-hidden">{{ t('common.units.linearFeet', 2) }}</dt>
      <dd class="m-0">{{ renderedLinearFeet }}</dd>
    </div>
  </dl>
</template>

<script setup lang="ts">
/**
 * A summary of line item metrics for search results
 *
 * Naming is hard. The difference between this and `LineItemSummarySearchResult`
 * is this component accepts the raw values and doesn't calculate anything.
 * `LineItemSummarySearchResult` accepts an array of line items and calculates
 * the values and passes them off to this one for display.
 */
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { translateNumberedEntity } from '@rygen/rygen-ui/i18n';
import { DimsUom, WeightUom } from '@rygen/rygen-ui/model';

const props = defineProps({
  totalWeight: {
    type: Number,
    default: () => undefined,
  },
  totalHandlingUnits: {
    type: Number,
    default: () => undefined,
  },
  handlingUnitLabel: {
    type: String,
    default: () => '',
  },
  totalLinear: {
    type: Number,
    default: () => undefined,
  },
});
const { totalWeight, totalHandlingUnits, handlingUnitLabel, totalLinear } =
  toRefs(props);

const { t } = useI18n();

const renderedWeight = computed(() => {
  return totalWeight?.value
    ? translateNumberedEntity(
        totalWeight.value,
        `uoms.weight.abbreviations.${WeightUom.POUND}`,
      )
    : undefined;
});

const renderedHandling = computed(() => {
  if (totalHandlingUnits?.value === undefined) {
    return undefined;
  }

  const uomText = handlingUnitLabel.value
    ? handlingUnitLabel.value
    : t('lineItems.handlingUnits');

  return `${totalHandlingUnits.value} ${uomText}`;
});

const renderedLinearFeet = computed(() => {
  return totalLinear?.value
    ? translateNumberedEntity(
        totalLinear.value,
        `uoms.length.abbreviations.${DimsUom.FOOT}`,
      )
    : undefined;
});
</script>
